import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// FIX: added fallback so app still works if .env is missing
// Backend listens on port 8000 in this repo.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// This is a LIVE TRADING MONITORING dashboard meant to stay open for hours, so a forced
// inactivity/absolute logout is wrong for it. The backend's 12h access token + 7-day refresh
// token (httpOnly cookies) are the single source of truth for the session. We keep the access
// token fresh with a silent /auth/refresh on a fixed interval, around the clock (not gated to
// market hours), and we do NOT hard-logout on UI idle or an absolute time cap.
const SILENT_REFRESH_INTERVAL_MS = 10 * 60 * 1000; // every 10 min (access token lifetime is 12h)

// Dual-layer session persistence: in-memory + client storage fallback.
// Guarantees sessions survive page reloads and browser privacy cookie drops.
let accessToken =
  typeof window !== 'undefined'
    ? localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || null
    : null;
let refreshToken =
  typeof window !== 'undefined'
    ? localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token') || null
    : null;

function storeTokens(access, refresh) {
  accessToken = access || null;
  refreshToken = refresh || null;
  if (typeof window !== 'undefined') {
    try {
      if (access) {
        localStorage.setItem('access_token', access);
        sessionStorage.setItem('access_token', access);
      }
      if (refresh) {
        localStorage.setItem('refresh_token', refresh);
        sessionStorage.setItem('refresh_token', refresh);
      }
    } catch (_) {}
  }
}

function clearTokens() {
  accessToken = null;
  refreshToken = null;
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('session_start');
    } catch (_) {}
  }
}

// --- Axios interceptors (module-level, run once) ---
axios.interceptors.request.use(config => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.withCredentials = true;
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, newToken) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(newToken);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config || {};
    const requestHeaders = originalRequest.headers || (originalRequest.headers = {});

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/register')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = { ...requestHeaders, Authorization: `Bearer ${token}` };
          originalRequest.withCredentials = true;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`,
          { refresh_token: refreshToken || (typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null) },
          { withCredentials: true }
        );
        const newAccess = data.access_token;
        storeTokens(newAccess, refreshToken);
        processQueue(null, newAccess);
        window.dispatchEvent(
          new CustomEvent('auth-state-changed', { detail: { type: 'refresh' } })
        );
        originalRequest.headers = { ...requestHeaders, Authorization: `Bearer ${newAccess}` };
        originalRequest.withCredentials = true;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        if (!originalRequest._authCheck && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// --- Hook ---
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// --- Provider ---
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);
  const loginInProgress = useRef(false);

  // --- Logout (stable — no external deps) ---
  const doLogout = useCallback(async (reason) => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`);
    } catch (err) {
      /* best-effort logout cleanup — server may be unreachable */
      void err;
    }
    clearTokens();
    setUser(false);
    if (reason) {
      sessionStorage.setItem('logout_reason', reason);
    }
  }, []); // stable: only uses module-level fns

  // --- Keep the session alive while logged in ---
  // No idle/absolute hard-logout: this is a live-trading monitoring dashboard meant to stay
  // open for hours. We just run a silent /auth/refresh every 10 min (around the clock) so the
  // access token stays fresh; the backend's 12h access + 7-day refresh cookies are the source
  // of truth, and the 401 interceptor handles a genuinely expired session.
  useEffect(() => {
    if (!user) return;

    const silentRefresh = async () => {
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`,
          { refresh_token: refreshToken || (typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null) },
          { withCredentials: true }
        );
        if (data?.access_token) {
          storeTokens(data.access_token, refreshToken);
          window.dispatchEvent(
            new CustomEvent("auth-state-changed", { detail: { type: "refresh" } })
          );
        }
      } catch {
        // If refresh fails (cookie truly expired), the 401 interceptor / next navigation logs out.
      }
    };

    void silentRefresh();
    const intervalId = setInterval(silentRefresh, SILENT_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [user]);

  // --- Initial auth check (cookies + bearer persistence across page refreshes) ---
  const checkAuth = useCallback(async () => {
    try {
      const reqHeaders = {};
      if (accessToken) {
        reqHeaders.Authorization = `Bearer ${accessToken}`;
      }
      const { data } = await axios.get(
        `${BACKEND_URL}/api/auth/me`,
        { _authCheck: true, timeout: 5000, headers: reqHeaders }
      );
      setUser(data);
      if (!sessionStorage.getItem('session_start')) {
        sessionStorage.setItem('session_start', String(Date.now()));
      }
    } catch (err) {
      if (!loginInProgress.current) {
        // If /me failed, attempt a 1-shot recovery using persisted refresh token
        try {
          const storedRefresh = refreshToken || (typeof window !== 'undefined' && localStorage.getItem('refresh_token'));
          if (storedRefresh) {
            const { data } = await axios.post(
              `${BACKEND_URL}/api/auth/refresh`,
              { refresh_token: storedRefresh },
              { withCredentials: true }
            );
            if (data?.access_token) {
              storeTokens(data.access_token, storedRefresh);
              const meRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${data.access_token}` },
                withCredentials: true,
              });
              setUser(meRes.data);
              return;
            }
          }
        } catch (_) {}

        clearTokens();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialized.current) { initialized.current = true; checkAuth(); }
  }, [checkAuth]);

  // --- Login / Register / Logout ---
  const login = useCallback(async (email, password) => {
    loginInProgress.current = true;
    try {
      // Debug instrumentation: verify what the browser is actually sending.
      // Do NOT log password itself.
      console.debug('[auth] login attempt', {
        backend: BACKEND_URL,
        email: (email || '').toString().trim().toLowerCase(),
        passwordLength: (password || '').toString().length,
      });

      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );

      storeTokens(data.access_token, data.refresh_token);
      sessionStorage.setItem('session_start', String(Date.now()));
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      window.dispatchEvent(
        new CustomEvent('auth-state-changed', { detail: { type: 'login' } })
      );
      return data;
    } finally {
      loginInProgress.current = false;
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/register`, { email, password, name });
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    sessionStorage.setItem('session_start', String(Date.now()));
    setUser(data);
    window.dispatchEvent(new CustomEvent("auth-state-changed", { detail: { type: "register" } }));
    return data;
  }, []);

  const logout = useCallback(async () => { await doLogout('manual'); }, [doLogout]);

  // Split memoization to keep dependency arrays ≤ 5
  const authActions = useMemo(
    () => ({ login, register, logout, checkAuth }),
    [login, register, logout, checkAuth]
  );

  const value = useMemo(
    () => ({ user, loading, ...authActions }),
    [user, loading, authActions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
